import { useState } from 'react'

import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { columns } from './constants'
import useQueryClearTurnoverHistory from '@/modules/manage-player/api/hooks/useQueryClearTurnoverHistory'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { Button } from '@/common/ui/button'
import { Download } from 'lucide-react'
import { useMutationExportPlayerCsv } from '@/modules/manage-player/api/hooks/useMutationExportPlayerCsv'
import { exportPlayerClearTurnoverHistoryCsv } from '@/modules/manage-player/api/services'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'

interface PlayerClearTurnoverHistoryTableProps {
	playerId: string
}

export default function PlayerClearTurnoverHistoryTable({
	playerId,
}: PlayerClearTurnoverHistoryTableProps) {
	const { pageSize, currentPage } = useTablePagination()
	const { dateFilter } = useDateFilter()
	const exportCsvMutation = useMutationExportPlayerCsv()
	const [showExportModal, setShowExportModal] = useState(false)

	const payload = {
		take: pageSize,
		skip: (currentPage - 1) * pageSize,
		playerId,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
	}

	const {
		data: clearTurnoverHistory,
		isLoading: isClearTurnoverHistoryLoading,
	} = useQueryClearTurnoverHistory(payload)

	const handleExportCsv = (amount: number) => {
		exportCsvMutation.mutate({
			filename: `clear_turnover_history_${playerId}`,
			exportFn: () =>
				exportPlayerClearTurnoverHistoryCsv({
					playerId,
					startDate: dateFilter.startDate || undefined,
					endDate: dateFilter.endDate || undefined,
					take: amount || clearTurnoverHistory?.total || 1000,
					skip: 0,
					columnMappings: {
						createdAt: 'วันที่',
						amount: 'จำนวนเงิน',
					},
				}),
		})
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-col items-end gap-4">
				<Button
					variant="outline"
					onClick={() => setShowExportModal(true)}
					disabled={
						exportCsvMutation.isPending || !clearTurnoverHistory?.data?.length
					}
					className="flex items-center gap-2"
					size="sm"
				>
					<Download className="h-4 w-4" />
					โหลด CSV
				</Button>
				<DateRangePicker />
				<DateRangeButtons />
			</div>
			<DataTablePaginate
				columns={columns}
				data={clearTurnoverHistory?.data || []}
				totalItems={clearTurnoverHistory?.total || 0}
				isLoading={isClearTurnoverHistoryLoading}
			/>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={clearTurnoverHistory?.total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>
		</div>
	)
}
